import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildHomeFaqFromTranslations,
  buildHomeJsonLd,
  buildPricingJsonLd,
  buildSubscriptionTiersFromTranslations,
} from '../seo/structuredData';
import type { PageSeo } from '../seo/routes';
import { normalizeLanguageCode } from '../seo/localizedUrl';

const SEO_ROUTE_KEYS: Record<string, string> = {
  '/': 'home',
  '/subscription': 'subscription',
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/edit': 'editor',
  '/preview': 'preview',
  '/decks': 'decks',
  '/decks/new': 'newDeck',
  '/profile': 'profile',
  '/login': 'login',
  '/reset-password': 'resetPassword',
};

const NOINDEX_PATHS = new Set([
  '/edit',
  '/preview',
  '/decks',
  '/decks/new',
  '/profile',
  '/login',
  '/reset-password',
]);

export const usePageSeo = (pathname: string): PageSeo => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const routeKey = SEO_ROUTE_KEYS[pathname] ?? 'home';
    const path = SEO_ROUTE_KEYS[pathname] ? pathname : '/';
    const index = !NOINDEX_PATHS.has(path);
    const language = normalizeLanguageCode(i18n.resolvedLanguage);
    const description = t(`seo.${routeKey}.description`);
    const tiers = buildSubscriptionTiersFromTranslations(t);

    const seo: PageSeo = {
      title: t(`seo.${routeKey}.title`),
      description,
      path,
      index,
      ogType: path === '/terms' || path === '/privacy' ? 'article' : 'website',
    };

    if (path === '/') {
      seo.jsonLd = buildHomeJsonLd({
        language,
        description,
        faq: buildHomeFaqFromTranslations(t),
        tiers,
      });
    } else if (path === '/subscription') {
      seo.jsonLd = buildPricingJsonLd(tiers);
    }

    return seo;
  }, [pathname, t, i18n.resolvedLanguage]);
};
